import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "@/store/auth.store";
import { isAllowedRoute } from "../../../utils/onboarding";

/**
 * OnboardingGuard - Wraps protected routes and redirects to onboarding if needed
 *
 * Allows certain routes (like /envelopes/new) to be visited during onboarding
 * so users can explore and return to the wizard.
 *
 * Location: frontend/src/pages/onboarding/components/OnboardingGuard.jsx
 *
 * Usage in App.jsx or routes:
 * ```jsx
 * <Route
 *   path="/dashboard"
 *   element={
 *     <OnboardingGuard>
 *       <Dashboard />
 *     </OnboardingGuard>
 *   }
 * />
 * ```
 *
 * Or wrap the entire protected layout:
 * ```jsx
 * <Route element={<OnboardingGuard><ProtectedLayout /></OnboardingGuard>}>
 *   <Route path="/dashboard" element={<Dashboard />} />
 *   <Route path="/envelopes" element={<Envelopes />} />
 * </Route>
 * ```
 */
export const OnboardingGuard = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    // Check if user needs onboarding and is not on an allowed route
    if (
      isAuthenticated &&
      user &&
      !user.hasCompletedOnboarding &&
      !isAllowedRoute(location.pathname)
    ) {
      // Store the intended destination for after onboarding
      sessionStorage.setItem("onboarding_redirect", location.pathname);
      navigate("/onboarding", { replace: true });
    }
  }, [isAuthenticated, user, location.pathname, navigate]);

  // If user hasn't completed onboarding and is not on an allowed route,
  // don't render children (they'll be redirected by the useEffect above)
  if (
    isAuthenticated &&
    user &&
    !user.hasCompletedOnboarding &&
    !isAllowedRoute(location.pathname)
  ) {
    return null;
  }

  return children;
};

export default OnboardingGuard;
